import React from "react";
import { v4 as uuidv4 } from "uuid";
import { PlusCircle, Trash2, ChevronDown } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { TooltipWrapper } from "../../../../components/ui/tooltip-wrapper";
import {
	RuleCondition,
	RuleConditionGroup,
	RuleOperator,
	FIELD_TYPE_OPERATORS,
	OPERATOR_LABELS,
} from "../../types/ruleTypes";
import { FormSection, FormField } from "../../types/formTypes";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";

interface ConditionBuilderProps {
	conditionGroup: RuleConditionGroup;
	formSections: FormSection[];
	onChange: (group: RuleConditionGroup) => void;
	showTypeSelector?: boolean;
}

export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
	conditionGroup,
	formSections,
	onChange,
	showTypeSelector = true,
}) => {
	// Get all fields from all sections (excluding title and description elements)
	const allFields: Array<FormField & { sectionTitle: string }> =
		formSections.flatMap((section) =>
			section.fields
				.filter((f) => f.type !== "title" && f.type !== "description")
				.map((field) => ({ ...field, sectionTitle: section.title }))
		);

	const handleAddCondition = () => {
		const newCondition: RuleCondition = {
			fieldId: allFields[0]?.id || "",
			operator: "equals",
			value: "",
		};

		onChange({
			...conditionGroup,
			conditions: [...conditionGroup.conditions, newCondition],
		});
	};

	const handleUpdateCondition = (
		index: number,
		updates: Partial<RuleCondition>
	) => {
		const newConditions = [...conditionGroup.conditions];
		newConditions[index] = {
			...(newConditions[index] as RuleCondition),
			...updates,
		};
		onChange({
			...conditionGroup,
			conditions: newConditions,
		});
	};

	const handleRemoveCondition = (index: number) => {
		onChange({
			...conditionGroup,
			conditions: conditionGroup.conditions.filter((_, i) => i !== index),
		});
	};

	const handleToggleLogic = () => {
		onChange({
			...conditionGroup,
			type: conditionGroup.type === "and" ? "or" : "and",
		});
	};

	const getFieldById = (fieldId: string) => {
		return allFields.find((f) => f.id === fieldId);
	};

	const getAvailableOperators = (
		field: FormField | undefined
	): RuleOperator[] => {
		if (!field) return [];
		return (
			FIELD_TYPE_OPERATORS[
				field.type as keyof typeof FIELD_TYPE_OPERATORS
			] || []
		);
	};

	const renderValueInput = (condition: RuleCondition, index: number) => {
		const field = getFieldById(condition.fieldId);
		const operator = condition.operator;

		// Don't show value input for is_empty/is_not_empty operators
		if (operator === "is_empty" || operator === "is_not_empty") {
			return null;
		}

		if (!field) {
			return (
				<Input
					type="text"
					value={(condition.value as string) || ""}
					onChange={(e) =>
						handleUpdateCondition(index, { value: e.target.value })
					}
					placeholder="Wert"
					className="flex-1"
					aria-label="Bedingungswert"
				/>
			);
		}

		// For select, radio, chips - show dropdown with options
		if (
			(field.type === "select" ||
				field.type === "radio" ||
				field.type === "chips") &&
			field.options &&
			field.options.length > 0
		) {
			return (
				<Select
					value={(condition.value as string) || ""}
					onValueChange={(value) =>
						handleUpdateCondition(index, { value })
					}
				>
					<SelectTrigger
						className="flex-1"
						aria-label="Wert auswählen"
					>
						<SelectValue placeholder="Wert auswählen" />
					</SelectTrigger>
					<SelectContent>
						{field.options.map((option) => (
							<SelectItem key={option} value={option}>
								{option}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			);
		}

		// For checkbox with includes/not_includes - show dropdown with options
		if (
			field.type === "checkbox" &&
			field.options &&
			field.options.length > 0
		) {
			return (
				<Select
					value={(condition.value as string) || ""}
					onValueChange={(value) =>
						handleUpdateCondition(index, { value })
					}
				>
					<SelectTrigger
						className="flex-1"
						aria-label="Wert auswählen"
					>
						<SelectValue placeholder="Wert auswählen" />
					</SelectTrigger>
					<SelectContent>
						{field.options.map((option) => (
							<SelectItem key={option} value={option}>
								{option}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			);
		}

		// Default text input
		return (
			<Input
				type={
					field.type === "date"
						? "date"
						: field.type === "time"
							? "time"
							: "text"
				}
				value={(condition.value as string) || ""}
				onChange={(e) =>
					handleUpdateCondition(index, { value: e.target.value })
				}
				placeholder="Wert"
				className="flex-1"
				aria-label="Bedingungswert"
			/>
		);
	};

	return (
		<div className="space-y-3">
			{showTypeSelector && (
				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-600">
						Bedingungstyp:
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={handleToggleLogic}
						className="h-8"
						data-testid="toggle-logic-button"
						aria-label={`Zu ${
							conditionGroup.type === "and" ? "ODER" : "UND"
						} wechseln`}
					>
						{conditionGroup.type === "and" ? "UND" : "ODER"}
					</Button>
					<span className="text-xs text-gray-500">
						(Alle Bedingungen müssen{" "}
						{conditionGroup.type === "and"
							? "erfüllt"
							: "mindestens eine erfüllt"}{" "}
						sein)
					</span>
				</div>
			)}
			<div className="space-y-2">
				{conditionGroup.conditions.map((condition, index) => {
					if ("type" in condition) {
						// Nested group - not implemented yet
						return null;
					}

					const field = getFieldById(condition.fieldId);
					const availableOperators = getAvailableOperators(field);

					return (
						<div
							key={index}
							className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded"
						>
							{/* Field Selector */}
							<Select
								value={condition.fieldId}
								onValueChange={(value) =>
									handleUpdateCondition(index, {
										fieldId: value,
										// Reset operator and value when field changes
										operator:
											FIELD_TYPE_OPERATORS[
												allFields.find(
													(f) => f.id === value
												)
													?.type as keyof typeof FIELD_TYPE_OPERATORS
											][0],
										value: "",
									})
								}
							>
								<SelectTrigger
									className="w-[200px]"
									aria-label="Feld auswählen"
								>
									<SelectValue placeholder="Feld auswählen" />
								</SelectTrigger>
								<SelectContent>
									{allFields.map((field) => (
										<SelectItem
											key={field.id}
											value={field.id}
										>
											{field.sectionTitle} / {field.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{/* Operator Selector */}
							<Select
								value={condition.operator}
								onValueChange={(value) =>
									handleUpdateCondition(index, {
										operator: value as RuleOperator,
									})
								}
							>
								<SelectTrigger
									className="w-[150px]"
									aria-label="Operator auswählen"
								>
									<SelectValue placeholder="Operator" />
								</SelectTrigger>
								<SelectContent>
									{availableOperators.map((op) => (
										<SelectItem key={op} value={op}>
											{OPERATOR_LABELS[op]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{/* Value Input */}
							{renderValueInput(condition, index)}

							{/* Remove Button */}
							<TooltipWrapper
								title="Bedingung entfernen"
								duration={150}
							>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleRemoveCondition(index)}
									className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
									aria-label="Bedingung entfernen"
									data-testid={`remove-condition-${index}`}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</TooltipWrapper>
						</div>
					);
				})}
			</div>
			<Button
				variant="outline"
				size="sm"
				onClick={handleAddCondition}
				className="gap-2"
				disabled={allFields.length === 0}
				data-testid="add-condition-button"
				aria-label="Bedingung hinzufügen"
			>
				<PlusCircle className="h-4 w-4" />
				Bedingung hinzufügen
			</Button>
		</div>
	);
};
